"use client";

import React, { createContext, useContext, useEffect, useState } from "react";

type LanguageContextType = {
    language: string;
    setLanguage: (lang: string) => void;
};

const LanguageContext = createContext<LanguageContextType>({
    language: "en",
    setLanguage: () => {},
});

const SUPPORTED_LANGUAGES = ["en", "es"] as const;
type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

function detectLanguage(): SupportedLanguage {
    // Only run on client side
    if (typeof window === "undefined") {
        return "en";
    }

    // First check localStorage
    const storedLanguage = localStorage.getItem("preferredLanguage");
    if (
        storedLanguage &&
        SUPPORTED_LANGUAGES.includes(storedLanguage as SupportedLanguage)
    ) {
        return storedLanguage as SupportedLanguage;
    }

    // Then check browser language
    const browserLang = navigator.language.split("-")[0];
    if (SUPPORTED_LANGUAGES.includes(browserLang as SupportedLanguage)) {
        return browserLang as SupportedLanguage;
    }

    // Default to English
    return "en";
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
    const [language, setLanguage] = useState<SupportedLanguage>("en");
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
        if (typeof window !== "undefined") {
            const detected = detectLanguage();
            if (detected !== language) setLanguage(detected);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update language in localStorage whenever it changes
    useEffect(() => {
        if (typeof window !== "undefined") {
            localStorage.setItem("preferredLanguage", language);
        }
    }, [language]);

    // Listen for language changes from other tabs/windows
    useEffect(() => {
        if (typeof window === "undefined") return;

        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === "preferredLanguage" && e.newValue) {
                const newLang = e.newValue as SupportedLanguage;
                if (SUPPORTED_LANGUAGES.includes(newLang)) {
                    setLanguage(newLang);
                }
            }
        };

        window.addEventListener("storage", handleStorageChange);
        return () => window.removeEventListener("storage", handleStorageChange);
    }, []);

    // Listen for browser language changes
    useEffect(() => {
        if (typeof window === "undefined") return;

        const handleLanguageChange = () => {
            const detectedLang = detectLanguage();
            if (detectedLang !== language) {
                setLanguage(detectedLang);
            }
        };

        window.addEventListener("languagechange", handleLanguageChange);
        return () =>
            window.removeEventListener("languagechange", handleLanguageChange);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSetLanguage = (lang: string) => {
        if (SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage)) {
            setLanguage(lang as SupportedLanguage);
        }
    };

    if (!mounted) {
        return null;
    }

    return (
        <LanguageContext.Provider
            value={{ language, setLanguage: handleSetLanguage }}
        >
            {children}
        </LanguageContext.Provider>
    );
}

export const useLanguage = () => useContext(LanguageContext);
